import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns'
import { Card, Divider, Stack, Typography } from '@mui/material'

import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'
import { IssueEntity } from '../../types/IssueEntity'
import { ExtendedWarrantEntity, filterEntities } from './utils'
import { CalendarTable } from './CalendarTable'
import LoadingIndicator from '../../components/LoadingIndicator'
import { CalendarFilter } from './CalendarFilter'
import { useAtom } from 'jotai'
import { calendarFiltersAtom } from '../../atoms/calendarAtom'
import { CalendarTableInfo } from './CalendarTableInfo'
import { LayoutContext } from '../../contexts/LayoutContext'
import { NAVBAR_DRAWER_WIDTH } from '../../layouts/DashboardBase'
import { useWatchListFilter } from '../../hooks/useWatchListFilter'

const CalendarView = () => {
    const [filteredEntities, setFilteredEntities] = useState<Array<IssueEntity | ExtendedWarrantEntity>>([])
    const [allEntities, setAllEntities] = useState<Array<IssueEntity | ExtendedWarrantEntity>>([])
    const [loading, setLoading] = useState(true)
    const { result: fetchedIssues } = useFetchEntitiesWithCache('issue')
    const { result: fetchedWarrants } = useFetchEntitiesWithCache('warrant')
    const [filters] = useAtom(calendarFiltersAtom)
    const layoutContext = useContext(LayoutContext)
    const { watchListFilter } = useWatchListFilter(filters.onlyInWatchLists.value)

    useEffect(() => {
        if (layoutContext.setPageWidth) layoutContext.setPageWidth('max-content')

        return () => {
            if (layoutContext.setPageWidth) layoutContext.setPageWidth('100%')
        }
    }, [layoutContext])

    const startDate = React.useMemo(() => subDays(startOfDay(new Date()), 30), [])
    const endDate = React.useMemo(() => addDays(endOfDay(new Date()), 60), [])

    useEffect(() => {
        if (!fetchedIssues?.length || !fetchedWarrants?.length) return

        const { allEntities, filteredEntities } = filterEntities(
            fetchedIssues,
            fetchedWarrants,
            startDate,
            endDate,
            filters,
        )
        setFilteredEntities(filteredEntities.filter(watchListFilter))
        setAllEntities(allEntities)
        setLoading(false)
    }, [endDate, watchListFilter, fetchedIssues, fetchedWarrants, filters, startDate])

    return (
        <React.Fragment>
            <Helmet title='Kalender' />

            <div
                style={{
                    position: 'sticky',
                    left: '239px',
                    width: `calc(100vw - ${NAVBAR_DRAWER_WIDTH}px - 24px - 24px - 10px)`,
                }}
            >
                <Typography variant='h3' gutterBottom display='inline'>
                    Kalender
                </Typography>

                <Divider sx={{ marginY: 6 }} />
                <CalendarFilter entities={allEntities} />
                <CalendarTableInfo />

                <LoadingIndicator
                    loading={loading}
                    label='Laddar kalenderdata...'
                    labelSx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                />
            </div>
            {allEntities.length > 0 && !loading ? (
                <Stack spacing={4}>
                    <Card
                        id='issue_calendar_card'
                        sx={{
                            overflow: 'initial',
                        }}
                    >
                        <CalendarTable filteredEntities={filteredEntities} />
                    </Card>
                </Stack>
            ) : null}
        </React.Fragment>
    )
}

export default CalendarView
